import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/motion";
import { STATES } from "@/constants/states";
import { usePageMeta } from "@/hooks/usePageMeta";

const StatesIndex = () => {
  usePageMeta(
    "X-Ray Shielding Requirements by State | Heartland Physics",
    "State-by-state guidance on X-ray machine registration and radiation shielding design requirements, written by a medical health physicist."
  );
  return (
    <>
      <section data-testid="states-index-page" className="border-b border-border bg-blueprint-grid pt-16">
        <div className="mx-auto max-w-7xl px-5 pb-14 pt-14 md:px-8 md:pb-20 md:pt-20">
          <Reveal y={12}>
            <p className="eyebrow">State-by-state guidance</p>
          </Reveal>
          <h1 className="mt-6 max-w-3xl font-heading text-4xl font-extrabold leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            <MaskedLines delay={0.1} lines={["Shielding requirements,", "state by state."]} />
          </h1>
          <Reveal delay={0.45}>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-ink/70 md:text-lg">
              Who regulates radiation machines in your state, what to file, and where a shielding design fits — plain language from the physicist who writes the reports.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-7xl px-5 py-14 md:px-8 md:py-20">
          <div className="border-t border-border" data-testid="states-index-list">
            {STATES.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.05}>
                <Link
                  to={`/shielding-requirements/${s.slug}`}
                  data-testid={`states-index-${s.slug}`}
                  className="group grid gap-1 border-b border-border py-6 transition-colors duration-200 hover:bg-paper md:grid-cols-[60px_1fr_auto] md:items-center md:gap-8 md:px-4"
                >
                  <span className="font-mono text-xs text-blueprint">{String(i + 1).padStart(2, "0")}</span>
                  <span>
                    <span className="block font-heading text-base font-bold tracking-tight text-ink transition-colors duration-200 group-hover:text-blueprint">
                      X-ray shielding requirements in {s.name}
                    </span>
                    <span className="mt-1 block max-w-2xl text-sm leading-relaxed text-ink/55">{s.quickAnswer.slice(0, 140)}…</span>
                  </span>
                  <ArrowRight size={15} className="text-blueprint transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <p className="mt-8 text-sm text-ink/55">
              All 50 states and the District of Columbia — each guide written from our state registration research.
            </p>
            <Link to="/request" data-testid="states-index-cta" className="group mt-4 inline-flex items-center gap-2 bg-blueprint px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-ink">
              Request a Shielding Design
              <ArrowUpRight size={15} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default StatesIndex;
